// Media-cache measurement + clearing for the Settings → Storage pane.
//
// "Media cache" = everything we persist locally so images/media render
// instantly and offline:
//   • `media_blobs`      — decrypted message media + resolved avatar bytes
//   • `media_thumbnails` — video posters + document first-page previews
//   • Cache Storage `ping-media-v1` — presigned remote images cached by the SW
// Clearing is safe: anything still referenced by a chat re-downloads on view.

import { db } from './db';

/** Must match the media cache name in `public/push-sw.js`. */
const SW_MEDIA_CACHE = 'ping-media-v1';

export interface MediaCacheBucket {
  readonly key: string;
  readonly label: string;
  readonly description?: string;
  readonly bytes: number;
}

export interface MediaCacheUsage {
  readonly totalBytes: number;
  readonly quotaBytes?: number;
  readonly buckets: MediaCacheBucket[];
}

async function mediaBlobsBytes(): Promise<number> {
  try {
    let sum = 0;
    // `sizeBytes` is a stored field, so this never has to read the blobs back.
    await db.media_blobs.each((r) => {
      sum += r.sizeBytes ?? 0;
    });
    return sum;
  } catch {
    return 0;
  }
}

async function mediaThumbsBytes(): Promise<number> {
  try {
    let sum = 0;
    await db.media_thumbnails.each((r) => {
      sum += r.blob?.size ?? 0;
    });
    return sum;
  } catch {
    return 0;
  }
}

async function swCacheBytes(): Promise<number> {
  try {
    if (typeof caches === 'undefined') return 0;
    const cache = await caches.open(SW_MEDIA_CACHE);
    const keys = await cache.keys();
    let sum = 0;
    for (const req of keys) {
      const res = await cache.match(req);
      if (!res) continue;
      const blob = await res.blob();
      sum += blob.size;
    }
    return sum;
  } catch {
    return 0;
  }
}

/** Measure every media-cache bucket + the origin storage quota. */
export async function computeMediaCacheUsage(): Promise<MediaCacheUsage> {
  const [images, thumbs, sw] = await Promise.all([
    mediaBlobsBytes(),
    mediaThumbsBytes(),
    swCacheBytes(),
  ]);
  let quotaBytes: number | undefined;
  try {
    if (typeof navigator !== 'undefined' && navigator.storage?.estimate) {
      const est = await navigator.storage.estimate();
      quotaBytes = est.quota;
    }
  } catch {
    /* quota is best-effort */
  }
  const buckets: MediaCacheBucket[] = [
    {
      key: 'images',
      label: 'Images & media',
      description: 'Decrypted photos, avatars and files kept for instant + offline viewing.',
      bytes: images,
    },
    {
      key: 'webImages',
      label: 'Cached remote images',
      description: 'Presigned images cached so they appear instantly on reload.',
      bytes: sw,
    },
    {
      key: 'thumbnails',
      label: 'Thumbnails',
      description: 'Video posters and document previews.',
      bytes: thumbs,
    },
  ];
  return {
    totalBytes: images + thumbs + sw,
    ...(quotaBytes !== undefined ? { quotaBytes } : {}),
    buckets,
  };
}

/** Drop every media-cache bucket. Conversations + messages are untouched. */
export async function clearMediaCache(): Promise<void> {
  try {
    await db.media_blobs.clear();
  } catch {
    /* best-effort */
  }
  try {
    await db.media_thumbnails.clear();
  } catch {
    /* best-effort */
  }
  try {
    if (typeof caches !== 'undefined') await caches.delete(SW_MEDIA_CACHE);
  } catch {
    /* best-effort */
  }
}
